<script setup lang="ts">
  import moment from "moment";
  import { Pencil, Trash2, TriangleAlert } from "lucide-vue-next";
  import { IndexTable, IndexTableName } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { ActionsDropdown } from "@/components/episto-ui/actions-dropdown";
  import { Tooltip } from "@/components/ui/tooltip";
  import ContractStatusBadge from "@/components/Contracts/ContractStatusBadge.vue";
  import CreditsProgressBar from "@/components/Contracts/CreditsProgressBar.vue";
  import ContractSurveysDetail from "@/components/Contracts/ContractSurveysDetail.vue";
  import { creditsLabel } from "@/utils/credits/creditRules";
  import type { Contract, ContractSurvey } from "@/types";

  withDefaults(
    defineProps<{
      contracts: Contract[];
      contractsCount?: number;
      currentPage?: number;
      expandedIds?: number[];
      canManage?: boolean;
    }>(),
    { contractsCount: 0, currentPage: 1, expandedIds: () => [], canManage: false },
  );

  const emit = defineEmits<{
    toggle: [id: number];
    edit: [contract: Contract];
    delete: [contract: Contract];
    "set-manual-credits": [payload: { survey: ContractSurvey; contract: Contract }];
    "page-change": [page: number];
  }>();

  const columns: DataTableColumn<Contract>[] = [
    { key: "name", label: "Contract" },
    { key: "status", label: "Status", width: 110 },
    { key: "credits", label: "Credits", width: 260 },
    { key: "period", label: "Period", width: 190 },
  ];

  const accountNames = (contract: Contract) => (contract.accounts ?? []).map((account) => account.name).join(", ");
  const formatMonth = (date: string | null) => (date ? moment(date).format("MMM YYYY") : "");
  const actionsFor = (contract: Contract) => [
    { isDelete: false, function: () => emit("edit", contract), text: "edit", icon: Pencil },
    {
      isDelete: true,
      function: () => emit("delete", contract),
      msgConfirm: "this contract",
      text: "delete",
      icon: Trash2,
      condition: (contract.surveys ?? []).length === 0,
    },
  ];
</script>

<template>
  <IndexTable
    :items="contracts"
    :columns="columns"
    :current-page="currentPage"
    :total="contractsCount"
    :expanded-keys="expandedIds"
    actions-class="w-[60px]"
    empty-message="No contracts found."
    @row-click="(contract) => emit('toggle', contract.id)"
    @page-change="emit('page-change', $event)">
    <template #cell-name="{ item }">
      <IndexTableName
        :name="item.name"
        :subtitle="accountNames(item)">
        <Tooltip
          v-if="item.is_over_allocated"
          side="top">
          <TriangleAlert class="size-3.5 shrink-0 text-status-warning-strong" />
          <template #content>
            <p class="font-medium">Over-allocated contract</p>
            <p>
              {{ item.credits_max_total }} {{ creditsLabel(item.credits_max_total) }} allocated across surveys, but the contract
              only holds {{ item.total_credits }}.
            </p>
          </template>
        </Tooltip>
      </IndexTableName>
    </template>
    <template #cell-status="{ item }">
      <ContractStatusBadge :status="item.status" />
    </template>
    <template #cell-credits="{ item }">
      <CreditsProgressBar
        :consumed="item.credits_consumed_total"
        :total="item.total_credits"
        :expired="item.status === 'expired'" />
    </template>
    <template #cell-period="{ item }">
      <span class="whitespace-nowrap text-muted-foreground">{{ formatMonth(item.starts_at) }} → {{ formatMonth(item.ends_at) }}</span>
    </template>
    <template
      v-if="canManage"
      #actions="{ item }">
      <div
        class="flex justify-end"
        @click.stop>
        <ActionsDropdown
          :actions="actionsFor(item)"
          :token="String(item.id)" />
      </div>
    </template>
    <template #expanded="{ item }">
      <ContractSurveysDetail
        :contract="item"
        :can-manage="canManage"
        @set-manual-credits="emit('set-manual-credits', $event)" />
    </template>
  </IndexTable>
</template>
